import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Subject } from 'rxjs';
import { SecretService } from './secret.service';

@Injectable({
  providedIn: 'root',
})
export class DataService {
  private dialogVisibleSub = new BehaviorSubject<any>({});
  public dialogVisibleObs$ = this.dialogVisibleSub.asObservable();
  menu = new Subject<any>();
  client = new Subject<any>();
  ticketTab = new Subject<any>();

  private menuAction = new BehaviorSubject(true);
  currentMenuAction = this.menuAction.asObservable();

  private shipmentList = new BehaviorSubject('');
  public currentShipmentList = this.shipmentList.asObservable();

  private customerList = new BehaviorSubject('');
  public currentCustomerList = this.customerList.asObservable();

  private claimList = new BehaviorSubject('');
  public currentClaimList = this.claimList.asObservable();

  private sidebarMenu = new BehaviorSubject('');
  public catchSidebarStatus = this.sidebarMenu.asObservable();

  private customerInvoice = new BehaviorSubject('');
  public customerInvoiceStatus = this.customerInvoice.asObservable();

  private salesrepCommission = new BehaviorSubject('');
  public salesrepCommissionStatus = this.salesrepCommission.asObservable();

  private carrierFactorList = new BehaviorSubject('');
  public carrierFactorListStatus = this.carrierFactorList.asObservable();

  private subjectKey = 'jobColor';
  private jobColorSubject = new BehaviorSubject<any>({});
  public jobColor = this.jobColorSubject.asObservable();

  private tlsScoreSubject = new BehaviorSubject<any>({});
  public tlsScore = this.tlsScoreSubject.asObservable();

  // private _defaultRows = new BehaviorSubject<Number>(10);
  // public defaultRows = this._defaultRows.asObservable();

  private _rowsSubject = new BehaviorSubject<number>(10);
  public rows$ = this._rowsSubject.asObservable();

  private _refreshDashboard = new Subject<void>();
  public refreshDashboard$ = this._refreshDashboard.asObservable();

  constructor(private secServ: SecretService) {
    this.getRowsFromLocalStorage();
  }

  updateMenu(action: any) {
    this.menuAction.next(action);
  }

  showFilteredShipments(status: any) {
    this.shipmentList.next(status);
  }

  showFilteredCustomers(status: any) {
    this.customerList.next(status);
  }

  showClaimData(status: any) {
    this.claimList.next(status);
  }

  hideSidebarMenu(status: any) {
    this.sidebarMenu.next(status);
  }

  showCustomerInvoice(action: any) {
    this.customerInvoice.next(action);
  }

  showCustomerCommission(action: any) {
    this.salesrepCommission.next(action);
  }

  showCarrierFactorList(status: any) {
    this.carrierFactorList.next(status);
  }

  setDialogVisible(data: any) {
    this.dialogVisibleSub.next(data);
  }

  setJobColor(data: any) {
    // console.log('Job color data updated:', data);
    // this.jobColorSubject.next(data);
    localStorage.setItem(this.subjectKey, JSON.stringify(data));
  }

  getSavedJobColor(): any {
    const savedData = localStorage.getItem(this.subjectKey);
    return savedData ? JSON.parse(savedData) : null;
  }

  updateTlsScore(data: any) {
    // console.log('Tls data:', data);
    this.tlsScoreSubject.next(data);
  }

  setRowsInLocalStorage(rows: number) {
    if (rows) {
      localStorage.setItem('tableRows', rows.toString());
      this._rowsSubject.next(rows);
    }
  }

  getRowsFromLocalStorage(): void {
    const savedRows = localStorage.getItem('tableRows');
    const rows = savedRows ? parseInt(savedRows, 10) : 10;
    this._rowsSubject.next(rows);
  }

  triggerDashboardRefresh(): void {
    this._refreshDashboard.next();
  }

  // Project Users Cache - stores multiple projects and their users
  private _projectUsersCache = new BehaviorSubject<Map<string, any[]>>(
    new Map()
  );
  public projectUsersCache$ = this._projectUsersCache.asObservable();

  private _revalidateProjectUsers = new Subject<string>();
  public revalidateProjectUsers$ = this._revalidateProjectUsers.asObservable();

  /**
   * Set users for a specific project in cache
   */
  setProjectUsers(projectId: string, users: any[]): void {
    const cache = this._projectUsersCache.value;
    cache.set(projectId, users);
    this._projectUsersCache.next(new Map(cache));
  }

  /**
   * Get users for a specific project from cache
   */
  getProjectUsers(projectId: string): any[] | null {
    return this._projectUsersCache.value.get(projectId) || null;
  }

  /**
   * Check if project users are cached
   */
  hasProjectUsers(projectId: string): boolean {
    return this._projectUsersCache.value.has(projectId);
  }

  /**
   * Remove a specific project from cache
   */
  removeProjectUsers(projectId: string): void {
    const cache = this._projectUsersCache.value;
    cache.delete(projectId);
    this._projectUsersCache.next(new Map(cache));
  }

  /**
   * Clear all cached project users
   */
  clearAllProjectUsers(): void {
    this._projectUsersCache.next(new Map());
  }

  /**
   * Trigger revalidation for a specific project
   * Components can subscribe to revalidateProjectUsers$ to refresh data
   */
  revalidateProjectUsers(projectId: string): void {
    this._revalidateProjectUsers.next(projectId);
  }

  /**
   * Check if current user is a member of a specific project
   */
  isCurrentUserProjectMember(projectId: string): boolean {
    const isDebugMode = false; // Set to true to enable debug logging

    if (isDebugMode) {
      console.log(
        '🔍 [DEBUG] Checking project membership for projectId:',
        projectId
      );
    }

    const userStr = localStorage.getItem('user_id');

    if (!userStr) {
      if (isDebugMode) console.log('❌ [DEBUG] No user found in localStorage');
      return false;
    }

    const projectUsers = this.getProjectUsers(projectId);
    if (!projectUsers) {
      if (isDebugMode) {
        console.log('❌ [DEBUG] No project users cached for this project');
        console.log(
          '💡 [DEBUG] Project users cache:',
          this._projectUsersCache.value
        );
      }
      return false;
    }

    if (isDebugMode) {
      console.log(
        '📋 [DEBUG] Project users found:',
        projectUsers.length,
        'members'
      );
    }

    try {
      const currentUserId = this.secServ.decryptLocalStorageData(userStr);

      if (isDebugMode) {
        console.log('👤 [DEBUG] Current user ID:', currentUserId);
        console.log(
          '👥 [DEBUG] Project members:',
          projectUsers.map((m: any) => ({
            userId: m.userId?._id || m.user_id || m._id,
            fullName: m.fullName || m.userId?.fullName || 'Unknown',
            email: m.email || m.userId?.email || 'Unknown',
          }))
        );
      }

      const isMember = projectUsers.some((member: any) => {
        const memberUserId = member.userId?._id || member.user_id || member._id;
        const matches = memberUserId === currentUserId;
        if (matches && isDebugMode) {
          console.log('✅ [DEBUG] MATCH FOUND! Member:', {
            userId: memberUserId,
            fullName: member.fullName || member.userId?.fullName,
            currentUserId: currentUserId,
          });
        }
        return matches;
      });

      if (isDebugMode) {
        console.log(
          isMember
            ? '✅ [DEBUG] User IS a project member'
            : '❌ [DEBUG] User is NOT a project member'
        );
      }
      return isMember;
    } catch (e) {
      console.error('❌ [DEBUG] Error checking project membership:', e);
      return false;
    }
  }
}
